import React, { useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import './EditorCategory.less';
import { IconButton } from 'components/buttons/IconButton';
import {
  ICategoryNode,
  getCategoryChildCount,
  NO_CATEGORY,
  IPropertyChild,
} from 'components/property-editors/utils/ProviderNodeUtils';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderNode';
import { EditorCategoryContents } from './EditorCategoryContents';
import { getDataPath } from 'components/Editors/utils/EditorDataAttributeHelpers';
import { EditorCategoryContext } from './EditorCategoryContext';
import { useTabbingContext } from 'contexts/TabbingContext';
import { CategoryType } from '@decisions/server/DecisionsFramework/Design/Properties/CategoryType';
import { CategoryAsEditor } from './CategoryAsEditor';
import { Dialog } from 'components/Dialogs';
import { HasOpenState } from 'hooks/HasOpenState';
import { Button } from 'components/buttons';
import { getEditorConfigValue } from '../utils/EditorConfigurationUtils';
import { ScrollProvider } from 'contexts/ScrollContext';

interface IEditorCategoryProps extends React.HTMLProps<{}> {
  child: ContextProviderNode;
  path: string[];
  depth: number;
}

const getClassName = (
  open: boolean,
  usePopupEditor: boolean,
  isNoCat: boolean,
  depth?: number,
  asEditor = false,
  className = ''
) =>
  classNames(
    EDITOR_CATEGORY_CLASS,
    {
      'dpc-editor-category--open': open && !asEditor && !usePopupEditor,
      'dpc-editor-category--popup-category': usePopupEditor && !asEditor,
      'dpc-editor-category--popup-editor': asEditor,
      'dpc-editor-category--depth-2': depth && depth >= 1,
      'dpc-editor-category--no-category': isNoCat,
      'dpc-editor-category--editor-style': asEditor,
    },
    className
  );

const getCaretClassName = (open = false, openBySearch = false) =>
  classNames('dpc-editor-category__caret', {
    'dpc-editor-category__caret--open': open || openBySearch,
  });

export const EDITOR_CATEGORY_CLASS = 'dpc-editor-category';

/**
 * Because it seems popup editor is sometimes assigned InitiallyCollapsed: false
 * - should this just be a server-side change? what's the risk of that?
 */
const getInitialOpenState = (openBySearch = false, initiallyCollapsed = false, usePopupEditor = false) =>
  (!initiallyCollapsed && !usePopupEditor) || (!usePopupEditor && openBySearch);

const getEditButtonClassName = (isOpen = false) =>
  classNames('dpc-editor-category__edit-button', {
    'dpc-editor-category__edit-button--active': isOpen,
  });

/**
 * Expandable Editing Group Category
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function EditorCategory({ child, depth, path }: IEditorCategoryProps) {
  const {
    Name,
    InitiallyCollapsed,
    OpenBySearch,
    UsePopupEditor,
    CategoryType: type,
  }: ICategoryNode = child.Node as ICategoryNode;
  const [embeddedChildCount, setEmbeddedChildCount] = useState(0);
  const [preserveChildren, setPreserveChildren] = useState(false);
  const initiallyOpen = getInitialOpenState(OpenBySearch, InitiallyCollapsed, UsePopupEditor);
  const { isOpen, toggleOpen, setIsOpen } = HasOpenState(initiallyOpen);
  const count = getCategoryChildCount(child) + embeddedChildCount;
  const dialogWidth = getEditorConfigValue<number>(child, 'dialogWidth') || 400;
  const dialogHeight = getEditorConfigValue<number>(child, 'dialogHeight') || 600;
  const [dialogElement, setDialogElement] = useState<HTMLElement | null>(null);

  const isNoCat = child.Node.Name === NO_CATEGORY;
  const asEditor = type === CategoryType.PopupEditor;

  // listen changes in search
  useEffect(() => {
    // TODO won't this call setState a bit eagerly?
    if (isOpen !== initiallyOpen) {
      setIsOpen(initiallyOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiallyOpen, setIsOpen, OpenBySearch]);

  const tabIndex = useTabbingContext();

  const contentProps = useMemo(
    () => ({
      child,
      path,
      depth,
      name: Name,
      usePopupEditor: UsePopupEditor,
    }),
    [Name, UsePopupEditor, child, depth, path]
  );

  const editButton = (
    <Button
      className={getEditButtonClassName(isOpen)}
      variant="link"
      onClick={toggleOpen}
      fontSize={'small'}
      tabIndex={tabIndex}
      dropDownOpened={isOpen}
    >
      Edit
    </Button>
  );

  const setDialogRef = (ref?: HTMLElement | null) => {
    // had to use legacyRef style function with setState, because the dialog's ref would be set here as undefined
    // before the dialog rendered. It's necessary to trigger a state update in order to update the ScrollProvider
    // or the overflow check the ScrollProvider is there to assist will never occur until the user updates an editor.
    if (ref && ref !== dialogElement) {
      setDialogElement(ref);
    }
  };

  return (
    <EditorCategoryContext.Provider
      value={{
        reportChildCount: setEmbeddedChildCount,
        setPreserveChildren,
      }}
    >
      <section
        className={getClassName(isOpen, UsePopupEditor, isNoCat, depth, asEditor, (child as IPropertyChild).className)}
        data-path={getDataPath([...path, Name])}
      >
        {!isNoCat && !asEditor && (
          <h3 className={`${EDITOR_CATEGORY_CLASS}__heading`} onClick={UsePopupEditor ? undefined : toggleOpen}>
            {!UsePopupEditor && (
              <IconButton className={getCaretClassName(isOpen)} iconClassName="dpc-editor-category__caret-icon" />
            )}
            {/* restore OpenBySearch here if we want to how many were found during search */}
            {Name} {!isOpen /* || OpenBySearch */ && ` (${count})`}
            {UsePopupEditor && editButton}
          </h3>
        )}
        {asEditor && <CategoryAsEditor node={child}>{editButton}</CategoryAsEditor>}
        {(isNoCat || isOpen || preserveChildren) &&
          (UsePopupEditor ? (
            <ScrollProvider scrollParent={dialogElement}>
              <Dialog
                contentRef={ref => setDialogRef((ref as HTMLElement) || null)}
                open={isOpen}
                styles={{ width: dialogWidth, height: dialogHeight }}
                dialogTitle={{ titleText: Name }}
                onClose={toggleOpen}
                disableFullScreenAction={true}
                keyboard={{ autoFocusFirst: true, trapTabbing: true }}
                dialogActions={{
                  actions: [
                    {
                      name: 'Done',
                      onClick: toggleOpen,
                    },
                  ],
                }}
              >
                <EditorCategoryContents {...contentProps} />
              </Dialog>
            </ScrollProvider>
          ) : (
            <EditorCategoryContents {...contentProps} renderHidden={!isOpen && preserveChildren} />
          ))}
      </section>
    </EditorCategoryContext.Provider>
  );
}

/**
 * MAYBE: We should have a list of slow editors and if the child of a category is one, we can flag it here
 *
 * Pre-render children
 *
 * This the other side of the coin for `setPreserveChildren`. This will also cause those children to be mounted
 * children even if the category has never been opened. This could be useful for components that are slow
 * to render (rich text comes to mind), but are not so expensive that you want to wait for the category to expand
 * before rendering them. This logically entails preserving those children too.
 *
 */
//  function preRenderChildren(node: ContextProviderNode): boolean {}
